import React, { useEffect, useMemo, useRef, type RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import './ScrollFloat.css';

gsap.registerPlugin(ScrollTrigger);

export interface ScrollFloatProps {
  children: string;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  containerClassName?: string;
  textClassName?: string;
  animationDuration?: number;
  ease?: string;
  scrollStart?: string;
  scrollEnd?: string;
  stagger?: number;
  highlightWords?: string[];
  highlightClassName?: string;
}

const ScrollFloat: React.FC<ScrollFloatProps> = ({
  children,
  scrollContainerRef,
  containerClassName = '',
  textClassName = '',
  animationDuration = 0.5,
  ease = 'back.out(1.5)',
  scrollStart = 'center bottom+=50%',
  scrollEnd = 'bottom bottom-=40%',
  stagger = 0.03,
  highlightWords = [],
  highlightClassName = ''
}) => {
  const containerRef = useRef<HTMLHeadingElement>(null);

  const splitText = useMemo(() => {
    const text = typeof children === 'string' ? children : '';
    const lines = text.split('\n');
    
    let charIndex = 0;
    return lines.map((line, lineIdx) => {
      const words = line.split(' ');
      const wordSpans = words.map((word, wordIdx) => {
        const isHighlighted = highlightWords.some(hw => 
          word.toLowerCase().replace(/[^a-z0-9]/g, '') === hw.toLowerCase().replace(/[^a-z0-9]/g, '')
        );
        
        const wordChars = word.split('').map((char) => {
          const idx = charIndex++;
          return (
            <span className="char" key={idx}>
              {char}
            </span>
          );
        });
        
        charIndex++; // for space
        
        return (
          <span 
            key={wordIdx} 
            className={isHighlighted ? highlightClassName : undefined}
            style={{ display: 'inline-flex' }}
            data-text={word}
          >
            {wordChars}
            {wordIdx < words.length - 1 && <span className="char">&nbsp;</span>}
          </span>
        );
      });

      return (
        <div key={lineIdx} className="scroll-float-line" style={{ display: 'block' }}>
          {wordSpans}
        </div>
      );
    });
  }, [children, highlightWords, highlightClassName]);

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).__PRERENDERING__) {
      return;
    }
    
    const el = containerRef.current;
    if (!el) return;

    const scroller = scrollContainerRef && scrollContainerRef.current ? scrollContainerRef.current : window;

    const charElements = el.querySelectorAll('.char');

    const anim = gsap.fromTo(
      charElements,
      {
        willChange: 'opacity, transform',
        opacity: 0,
        yPercent: 120,
        scaleY: 2.3,
        scaleX: 0.7,
        transformOrigin: '50% 0%'
      },
      {
        duration: animationDuration,
        ease: ease,
        opacity: 1,
        yPercent: 0,
        scaleY: 1,
        scaleX: 1,
        stagger: stagger,
        scrollTrigger: {
          trigger: el,
          scroller: scroller as HTMLElement | Window,
          start: scrollStart,
          end: scrollEnd,
          once: true
        }
      }
    );

    return () => {
      if (anim.scrollTrigger) anim.scrollTrigger.kill();
      anim.kill();
    };
  }, [scrollContainerRef, animationDuration, ease, scrollStart, scrollEnd, stagger]);

  return (
    <h2 ref={containerRef} className={`scroll-float ${containerClassName}`}>
      <span className={`scroll-float-text ${textClassName}`}>{splitText}</span>
    </h2>
  );
};

export default ScrollFloat;
